import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { buildWorkspacePayload } from "@/hooks/useWorkspaceAutoSave";
import {
  clearHydrating,
  createWorkspace,
  deleteWorkspace,
  fetchWorkspace,
  fetchWorkspaceList,
  saveWorkspace,
  setActiveWorkspaceOnServer,
  setCurrentName,
} from "@/state/slices/workspacesSlice";
import styles from "./WorkspaceSwitcher.module.css";

/** Chip + dropdown for switching between named workspaces. Lives in
 *  the TabBar to the right of the "+" button. New / Rename / Delete
 *  inline so the user doesn't get bounced into a modal for a quick
 *  workspace operation. */
export function WorkspaceSwitcher() {
  const dispatch = useAppDispatch();
  const list = useAppSelector((s) => s.workspaces.list);
  const currentName = useAppSelector((s) => s.workspaces.currentName);
  const saving = useAppSelector((s) => s.workspaces.saving);
  const error = useAppSelector((s) => s.workspaces.error);
  const tabs = useAppSelector((s) => s.tabs.tabs);
  const activeTabId = useAppSelector((s) => s.tabs.activeTabId);
  const splitTabId = useAppSelector((s) => s.tabs.splitTabId);
  const splitRatio = useAppSelector((s) => s.tabs.splitRatio);
  const paneByTabId = useAppSelector((s) => s.tabs.paneByTabId);
  const prefs = useAppSelector((s) => s.ui.prefs);
  const uiByTab = useAppSelector((s) => s.ui.byTab);

  const [open, setOpen] = useState(false);
  const [newDraft, setNewDraft] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const newInputRef = useRef<HTMLInputElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Position the portal-rendered menu directly under the chip. The
  // TabBar's `overflow-y: hidden` clips anything rendered inside it,
  // so we render to document.body with position:fixed and compute
  // coords from the chip's bounding rect. Recomputed when the menu
  // opens; window resize would invalidate it but resizing the
  // viewport while the menu is open is rare and the next reopen
  // recalculates.
  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const el = chipRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, [open]);

  // Close dropdown on outside click / Esc. Outside-click must check
  // BOTH the wrap (chip) and the portaled menu — clicking inside the
  // portal would otherwise be treated as outside-the-wrap and close.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      const inWrap = wrapRef.current?.contains(t);
      const inMenu = menuRef.current?.contains(t);
      if (!inWrap && !inMenu) {
        setOpen(false);
        setNewDraft(null);
        setRenameDraft(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setNewDraft(null);
        setRenameDraft(null);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Depend on the OPEN-ness booleans (not the draft value) so the
  // focus/select fires once when the input first appears, NOT on
  // every keystroke. Selecting on each keystroke re-selected all
  // text — the next typed character then replaced the selection,
  // and the user could only ever see a single-character draft.
  const newOpen = newDraft !== null;
  const renameOpen = renameDraft !== null;
  useEffect(() => {
    if (newOpen) newInputRef.current?.focus();
  }, [newOpen]);
  useEffect(() => {
    if (renameOpen) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [renameOpen]);

  const handleSwitch = async (name: string) => {
    if (name === currentName) {
      setOpen(false);
      return;
    }
    // Mark hydration so the autosave hook doesn't fire from the
    // about-to-be-replaced state. fetchWorkspace clears it after
    // hydrateWorkspace lands.
    setOpen(false);
    try {
      await dispatch(fetchWorkspace(name)).unwrap();
      await dispatch(setActiveWorkspaceOnServer(name)).unwrap();
    } catch {
      dispatch(clearHydrating());
    }
  };

  const handleCreate = async () => {
    const name = (newDraft ?? "").trim();
    if (!name) return;
    if (list.some((w) => w.name === name)) {
      // Silently fall through to switch to the existing one — the
      // user can't accidentally clobber a workspace they didn't
      // intend to. They'd need to explicitly Delete first.
      await handleSwitch(name);
      setNewDraft(null);
      return;
    }
    // Snapshot the current chart state as the starting point for
    // the new workspace. Without this, "New" would save an empty
    // default which is rarely what the user wants.
    const payload = buildWorkspacePayload(
      tabs,
      activeTabId,
      uiByTab,
      splitTabId,
      splitRatio,
      paneByTabId,
      prefs,
    );
    try {
      await dispatch(createWorkspace({ name, payload })).unwrap();
      // currentName is already set by createWorkspace.fulfilled but
      // keep this for belt-and-suspenders parity with handleSwitch.
      dispatch(setCurrentName(name));
      setNewDraft(null);
      setOpen(false);
    } catch (err) {
      // Log so the browser console shows what actually failed
      // (network error, 4xx response, etc.) — the visible error
      // banner below shows the message but the console has the full
      // stack. Re-fetch the list so whatever HAS landed on the
      // server is reflected, even if the chain failed midway.
      // eslint-disable-next-line no-console
      console.error("Create workspace failed:", err);
      dispatch(fetchWorkspaceList());
      // Keep the dropdown open + draft populated so the user can
      // see the error message + retry without retyping.
    }
  };

  const handleRename = async () => {
    if (!currentName) return;
    const newName = (renameDraft ?? "").trim();
    if (!newName || newName === currentName) {
      setRenameDraft(null);
      return;
    }
    if (list.some((w) => w.name === newName)) {
      // Don't silently overwrite — tell the user so they don't
      // think the rename succeeded and end up with two workspaces.
      // Previously this returned silently, which is how users
      // ended up with duplicates ("I renamed but both exist now").
      window.alert(
        `A workspace named "${newName}" already exists. Pick a ` +
          `different name, or delete the existing one first.`,
      );
      setRenameDraft(null);
      return;
    }
    // Rename = save under new name + delete old + set active. The
    // backend doesn't have a true atomic rename; this two-step is
    // fine for single-user concurrency.
    const payload = buildWorkspacePayload(
      tabs,
      activeTabId,
      uiByTab,
      splitTabId,
      splitRatio,
      paneByTabId,
      prefs,
    );
    try {
      await dispatch(saveWorkspace({ name: newName, payload })).unwrap();
      await dispatch(deleteWorkspace(currentName)).unwrap();
      await dispatch(setActiveWorkspaceOnServer(newName)).unwrap();
      await dispatch(fetchWorkspaceList()).unwrap();
      dispatch(setCurrentName(newName));
    } catch {
      // Error surfaced via slice; partial state possible if the
      // delete fails after the save — user can clean up manually.
    }
    setRenameDraft(null);
    setOpen(false);
  };

  const handleDelete = async () => {
    if (!currentName) return;
    const ok = window.confirm(
      `Delete workspace "${currentName}"? This can't be undone.`,
    );
    if (!ok) return;
    try {
      await dispatch(deleteWorkspace(currentName)).unwrap();
      dispatch(setCurrentName(null));
      await dispatch(fetchWorkspaceList()).unwrap();
    } catch {
      // surfaced via slice
    }
    setOpen(false);
  };

  const chipLabel = currentName ?? "Unsaved";
  const chipDot =
    currentName !== null ? (
      <span className={styles.chipDot} aria-hidden />
    ) : null;

  const menu = open && menuPos ? (
    <div
      ref={menuRef}
      className={styles.menu}
      role="menu"
      style={{ top: menuPos.top, right: menuPos.right }}
    >
      <div className={styles.sectionLabel}>Workspaces</div>
      <div className={styles.section}>
        {list.length === 0 ? (
          <div
            className={`${styles.item} ${styles.itemDisabled}`}
            aria-disabled
          >
            No saved workspaces yet
          </div>
        ) : (
          list.map((w) => (
            <button
              key={w.name}
              type="button"
              role="menuitem"
              className={`${styles.item} ${
                w.name === currentName ? styles.itemActive : ""
              }`}
              onClick={() => handleSwitch(w.name)}
              title={
                w.name === currentName
                  ? "Currently editing this workspace"
                  : `Switch to "${w.name}"`
              }
            >
              <span>{w.name}</span>
              {w.name === currentName ? <span>●</span> : null}
            </button>
          ))
        )}
      </div>

      <div className={styles.divider} />

      {newDraft !== null ? (
        <input
          ref={newInputRef}
          className={styles.input}
          type="text"
          value={newDraft}
          placeholder="Workspace name…"
          onChange={(e) => setNewDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleCreate();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setNewDraft(null);
            }
          }}
          onBlur={handleCreate}
        />
      ) : (
        <button
          type="button"
          role="menuitem"
          className={styles.item}
          onClick={() => setNewDraft("")}
        >
          New workspace from current state…
        </button>
      )}

      {currentName ? (
        renameDraft !== null ? (
          <input
            ref={renameInputRef}
            className={styles.input}
            type="text"
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleRename();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setRenameDraft(null);
              }
            }}
            onBlur={handleRename}
          />
        ) : (
          <button
            type="button"
            role="menuitem"
            className={styles.item}
            onClick={() => setRenameDraft(currentName)}
          >
            Rename "{currentName}"…
          </button>
        )
      ) : null}

      {currentName ? (
        <button
          type="button"
          role="menuitem"
          className={`${styles.item} ${styles.itemDanger}`}
          onClick={handleDelete}
        >
          Delete "{currentName}"
        </button>
      ) : null}

      {error ? (
        <>
          <div className={styles.divider} />
          <div className={styles.errorBox} role="alert">
            {error}
          </div>
        </>
      ) : null}
    </div>
  ) : null;

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <button
        ref={chipRef}
        type="button"
        className={`${styles.chip} ${currentName ? "" : styles.chipUnsaved}`}
        onClick={() => setOpen((v) => !v)}
        title={
          currentName
            ? `Workspace: ${currentName}${saving ? " (saving…)" : ""}`
            : "No workspace — click to create one"
        }
        data-tour="workspace"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {chipDot}
        {chipLabel}
        {saving ? <span className={styles.savingBadge}>·…</span> : null}
        <span aria-hidden>▾</span>
      </button>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
}
